import clsx from 'clsx';
import { motion, type Transition, useReducedMotion } from 'motion/react';
import type React from 'react';

import { Truncatable } from '@gitbook/spine-react';

/**
 * Slide-up: a duration-bound spring, so the tray overshoots a touch and settles into place.
 */
const SHOW_TRANSITION: Transition = { duration: 0.15, ease: 'easeIn' };

/**
 * Slide-down: a decelerating ease instead — a bounce on the way down would read as the tray
 * colliding with the composer it is disappearing behind.
 */
const HIDE_TRANSITION: Transition = { duration: 0.15, ease: 'easeOut' };

/**
 * What the conversation edits in — a change request, or a space edited live — shown as a tray tucked
 * behind the composer.
 *
 * Deliberately not one of the small pills used for pages, selections and files: this is the
 * conversation's working target rather than one reference among several, so it gets its own
 * full-width surface. The tray shares the composer's corner radius and slides behind it — the
 * composer paints above, so hiding the tray is a slide down behind it rather than a disappearance.
 *
 * Holds whatever describes the target: {@link EditTargetHeader} names it, and anything under that —
 * the page being edited in it, the pages it has been edited in — goes on its own line below.
 */
export function EditTargetTray(props: {
    /**
     * Slides the tray down behind the composer instead of unmounting it, so removing the context
     * animates — and putting it back slides it up from where it went.
     */
    hidden?: boolean;
    /**
     * Play the slide up on mount, for a tray that appears part-way through a conversation rather
     * than being there from the start.
     */
    animateIn?: boolean;
    /**
     * Render the tray as the last thing in the chat, for a conversation with no composer to tuck
     * behind: it closes its own bottom corners and keeps the composer's padding instead of the
     * overhang that would otherwise hang off the end of the conversation.
     */
    standalone?: boolean;
    children: React.ReactNode;
}) {
    const { hidden = false, animateIn = false, standalone = false, children } = props;
    const prefersReducedMotion = useReducedMotion();
    const instant: Transition = { duration: 0 };

    return (
        // The wrapper animates the space the tray occupies; the tray keeps its natural height and
        // overflows downward — behind the composer, which paints above it — so collapsing the
        // space plays as the tray sliding down behind the composer. Nothing is clipped, which is
        // why neither element carries an overflow.
        <motion.div
            initial={animateIn ? 'hidden' : false}
            animate={hidden ? 'hidden' : 'visible'}
            // Still mounted while hidden, so keep it out of the tab order and the accessibility tree.
            inert={hidden}
            // Unmounting — the offer was taken up by the first message — plays the same slide
            // behind the composer as hiding, through the chat's presence boundary.
            exit="hidden"
            variants={{
                visible: {
                    height: 'auto',
                    opacity: 1,
                    transition: prefersReducedMotion ? instant : SHOW_TRANSITION,
                },
                hidden: {
                    height: 0,
                    opacity: 0,
                    transition: prefersReducedMotion ? instant : HIDE_TRANSITION,
                },
            }}
        >
            <div
                className={clsx(
                    'group/banner flex flex-col gap-1.5 select-none',
                    standalone
                        ? // A closed card, so it sits evenly in the window: no negative margin,
                          // leaving the 2 its container already keeps on the sides and below —
                          // the overhang widens the tucked tray past the composer, and there is no
                          // composer here to line up with.
                          //
                          // The rest follows from that inset: `xl` is the radius concentric with
                          // the dock window's `1.25rem` at 2, and the padding matches that radius
                          // on all four sides so the content clears the curve evenly. `pl-1`
                          // rather than the `p-3` it overrides, because the rows carry their own
                          // `pl-2` on top of it.
                          //
                          // Flat, too: the gradient exists to fade the tray into the composer it
                          // tucks behind, and there is no composer under this one.
                          'rounded-xl bg-neutral-ui p-3 pl-1'
                        : // The overhang is what the composer paints over: the tray keeps 2 of
                          // padding showing above it and hides the rest behind it.
                          '-mx-1 -mb-8 rounded-t-xl bg-linear-to-t from-(--neutral-2) from-20% to-(--neutral-3) pt-2 pr-3 pb-10 pl-1'
                )}
            >
                {children}
            </div>
        </motion.div>
    );
}

/**
 * Top line of a tray, naming the target.
 *
 * `children` are the controls at the end of the row — removing the target, or summarizing what has
 * been done in it — which is the only thing the trays differ by.
 */
export function EditTargetHeader(props: {
    /**
     * Rendered already, sized `size-3.5`. Omitted while the target is unknown, which is what an
     * agent reporting none leaves.
     */
    icon?: React.ReactNode;
    /** Name of the target: a change request's subject, or the space's title. */
    label: string;
    /** Human-facing change request number, e.g. 42 for "#42". Omitted for a space. */
    number?: number;
    children?: React.ReactNode;
}) {
    const { icon, label, number, children } = props;

    return (
        <div className="relative flex items-center gap-2 pl-2">
            {icon}
            {number !== undefined ? (
                <span className="shrink-0 text-ui-standout font-[440] text-neutral-low">
                    #{number}
                </span>
            ) : null}
            <Truncatable className="min-w-0 flex-1 text-ui-standout text-neutral-high">
                {label}
            </Truncatable>
            {children}
        </div>
    );
}
